import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Newspaper, RefreshCw, Settings, Sparkles } from 'lucide-react'
import { Link } from 'react-router-dom'
import { JarvisCard } from '@/components/JarvisCard'
import { useTodayBriefing, useCurrentUser } from '@/lib/hooks'
import { apiClient, unwrap } from '@/lib/apiClient'
import type { Briefing } from '@/types'

const dateLabel = new Date().toLocaleDateString('en-US', {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
})

export function BriefingCard() {
  const me = useCurrentUser()
  const { data, isLoading } = useTodayBriefing()
  const queryClient = useQueryClient()
  const [generated, setGenerated] = useState<Briefing | null>(null)

  const generate = useMutation({
    mutationFn: () =>
      unwrap<{ briefing: Briefing | null; reason?: string }>(
        apiClient.post('/briefing/generate', {}),
      ),
    onSuccess: (res) => {
      setGenerated(res.briefing ?? null)
      queryClient.invalidateQueries({ queryKey: ['briefing', 'today'] })
    },
  })

  const briefing = generated ?? data?.briefing ?? null
  const briefingEnabled = me.data?.briefingEnabled
  const hasTopics = (me.data?.briefingTopics?.length ?? 0) > 0

  if (isLoading && !briefing) {
    return (
      <JarvisCard>
        <CardHeader />
        <div className="text-jarvis-muted text-sm py-6 text-center">Loading…</div>
      </JarvisCard>
    )
  }

  // First-time / not-enabled empty state — strong CTA to set up briefings
  if (!briefing && !briefingEnabled) {
    return (
      <JarvisCard>
        <CardHeader />
        <div className="mt-4 flex items-start gap-3">
          <Sparkles size={18} className="text-jarvis-blue mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <p>
              Wake up to a 2-minute brief on the news that actually affects your business.
              Pick your topics — Jarvis reads the internet at 7 a.m. so you don't have to.
            </p>
            <Link
              to="/settings#briefing"
              className="mt-3 inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-jarvis-blue text-jarvis-surface hover:bg-jarvis-blue-bright transition-colors"
            >
              Set up briefings →
            </Link>
          </div>
        </div>
      </JarvisCard>
    )
  }

  // Enabled but never run — show "Generate now" CTA
  if (!briefing && briefingEnabled) {
    return (
      <JarvisCard>
        <CardHeader />
        <div className="mt-4 text-sm">
          {hasTopics ? (
            <>
              <p className="text-jarvis-muted">
                No briefing yet today. The scheduled run is at 7 a.m. UTC, or
                generate one on demand:
              </p>
              <button
                onClick={() => generate.mutate()}
                disabled={generate.isPending}
                className="mt-3 inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-jarvis-blue text-jarvis-surface hover:bg-jarvis-blue-bright disabled:opacity-50 transition-colors"
              >
                <RefreshCw
                  size={12}
                  className={generate.isPending ? 'animate-spin' : ''}
                />
                {generate.isPending ? 'Generating…' : 'Generate now'}
              </button>
            </>
          ) : (
            <>
              <p className="text-jarvis-muted">
                Briefings are on, but you have no topics yet.
              </p>
              <Link
                to="/settings#briefing"
                className="mt-3 inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-jarvis-blue text-jarvis-surface hover:bg-jarvis-blue-bright transition-colors"
              >
                Add topics →
              </Link>
            </>
          )}
        </div>
      </JarvisCard>
    )
  }

  // We have a briefing — render it. The early-return chain above guarantees
  // `briefing` is non-null here, but TS isn't narrowing it through the
  // compound conditions — explicit guard keeps the compiler happy.
  if (!briefing) return null

  return (
    <JarvisCard>
      <CardHeader>
        <div className="flex items-center gap-2">
          <button
            onClick={() => generate.mutate()}
            disabled={generate.isPending}
            title="Regenerate"
            className="text-xs flex items-center gap-1 px-2 py-1 rounded-md text-jarvis-muted hover:text-jarvis-text hover:bg-jarvis-surface-2 transition-colors"
          >
            <RefreshCw
              size={12}
              className={generate.isPending ? 'animate-spin' : ''}
            />
            {generate.isPending ? 'Generating…' : 'Regenerate'}
          </button>
          <Link
            to="/settings#briefing"
            title="Edit topics"
            className="text-xs flex items-center gap-1 px-2 py-1 rounded-md text-jarvis-muted hover:text-jarvis-text hover:bg-jarvis-surface-2 transition-colors"
          >
            <Settings size={12} />
            Topics
          </Link>
        </div>
      </CardHeader>
      <div className="mt-3 text-xs text-jarvis-muted">
        {briefing.topics.length} topic{briefing.topics.length === 1 ? '' : 's'} ·{' '}
        {new Date(briefing.content.generatedAt).toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
        })}
      </div>
      <div className="mt-3 text-sm whitespace-pre-wrap leading-relaxed">
        {briefing.content.text}
      </div>
    </JarvisCard>
  )
}

function CardHeader({ children }: { children?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-2">
        <Newspaper size={16} className="text-jarvis-blue" />
        <h2 className="text-xs uppercase tracking-widest text-jarvis-muted">
          Today's Briefing · {dateLabel}
        </h2>
      </div>
      {children}
    </div>
  )
}
